import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  class: "flex min-h-[4.25rem] items-center rounded-xl border border-gray-100 bg-gray-50 px-4 py-3",
  "data-testid": "doctor-invite-card"
}
const _hoisted_2 = { class: "flex w-full items-center gap-3 min-w-0" }
const _hoisted_3 = { class: "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white text-lg text-gray-500" }
const _hoisted_4 = { class: "flex min-w-0 flex-1 flex-col gap-0.5" }
const _hoisted_5 = ["title"]
const _hoisted_6 = {
  "data-testid": "doctor-invite-summary",
  class: "flex items-center gap-1 text-xs text-gray-600"
}
const _hoisted_7 = {
  key: 0,
  class: "flex shrink-0 flex-wrap justify-end gap-2"
}
const _hoisted_8 = ["disabled"]

import { computed, ref } from 'vue'
import {
  mdiCheck,
  mdiCheckCircle,
  mdiClockOutline,
  mdiCloseCircle,
  mdiContentCopy,
  mdiEmailSyncOutline,
  mdiMinusCircleOutline,
  mdiOfficeBuildingOutline
} from '@mdi/js'
import { useToast } from 'vue-toastification'

import SVGIcon from '@/components/SVGIcon.vue'
import ConfirmActionModal from '@/components/designSystem/modals/confirm.delete.action.vue'
import type { DoctorDetailCompanyLink } from '@/Network/Types/Responses/CompanyResponses'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import { useDoctorDetailStore } from '@/pinia-store/useDoctorDetailStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { getInviteSummary } from '../../helpers/doctorStatus'
import type { InviteSummaryTone } from '../../helpers/doctorStatus'
import { getInviteErrorMessage } from '../../helpers/inviteErrors'
import { useInviteLink } from '../../helpers/useInviteLink'

const ACTION_CLASS =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-primary30 shadow-sm transition-colors hover:border-primary30 hover:bg-primary5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary30 disabled:cursor-not-allowed disabled:opacity-60'


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanyInviteRow',
  props: {
    link: {}
  },
  setup(__props: any) {

const props = __props;



const toast = useToast()
const detailStore = useDoctorDetailStore()
const permissionsStore = usePermissionsStore()

// Mesmo botão compacto do "Ver arquivo" dos documentos do médico.
const inviteSummary = computed(() => getInviteSummary(props.link.invite))

const TONE_ICON: Record<InviteSummaryTone, { path: string; color: string }> = {
  positive: { path: mdiCheckCircle, color: 'text-sucess' },
  warning: { path: mdiClockOutline, color: 'text-warning' },
  negative: { path: mdiCloseCircle, color: 'text-negative' },
  neutral: { path: mdiMinusCircleOutline, color: 'text-gray-400' }
}

const { inviteUrl, isCopied, copy } = useInviteLink(() => props.link.invite?.hash)

// Somente DECLINED por ora; EXPIRED/CANCELLED dependem de suporte no backend.
const RESENDABLE_DOCTOR_STATUSES = new Set<string>([CompanyDoctorInviteStatusEnum.DECLINED])

const canResend = computed(() => {
  const status = props.link.invite?.doctorStatus
  if (!status || !RESENDABLE_DOCTOR_STATUSES.has(status)) return false
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.CompanyDoctorInviteCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const isResending = ref(false)
const showResendConfirm = ref(false)

async function handleResend() {
  try {
    isResending.value = true
    await detailStore.resendInvite(props.link.company.id)
    showResendConfirm.value = false
    toast.success('Convite reenviado com sucesso.')
  } catch (error) {
    console.error('Erro ao reenviar convite:', error)
    // Guard de domínio (convite já ativo → mensagem curada): exibido direto,
    // pois como fallback de notifyError a mensagem curada nunca apareceria.
    toast.error(
      getInviteErrorMessage(error, 'Não foi possível reenviar o convite. Tente novamente.')
    )
  } finally {
    isResending.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("span", _hoisted_3, [
          _createVNode(SVGIcon, { path: _unref(mdiOfficeBuildingOutline) }, null, 8, ["path"])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("p", {
            "data-testid": "doctor-invite-company",
            class: "text-sm font-semibold text-gray-900 capitalize truncate",
            title: _ctx.link.company.name
          }, _toDisplayString(_ctx.link.company.name.toLowerCase()), 9, _hoisted_5),
          _createElementVNode("p", _hoisted_6, [
            _createVNode(SVGIcon, {
              path: TONE_ICON[inviteSummary.value.tone].path,
              class: _normalizeClass(TONE_ICON[inviteSummary.value.tone].color)
            }, null, 8, ["path", "class"]),
            _createTextVNode(" " + _toDisplayString(inviteSummary.value.label), 1)
          ])
        ]),
        (_unref(inviteUrl) || canResend.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (_unref(inviteUrl))
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: _normalizeClass(ACTION_CLASS),
                    "data-testid": "doctor-copy-invite-link-button",
                    onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(copy) && _unref(copy)(...args)))
                  }, [
                    _createVNode(SVGIcon, {
                      path: _unref(isCopied) ? _unref(mdiCheck) : _unref(mdiContentCopy),
                      class: "text-sm"
                    }, null, 8, ["path"]),
                    _createTextVNode(" " + _toDisplayString(_unref(isCopied) ? 'Link copiado!' : 'Copiar link do convite'), 1)
                  ]))
                : _createCommentVNode("", true),
              (canResend.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    type: "button",
                    class: _normalizeClass(ACTION_CLASS),
                    disabled: isResending.value,
                    "data-testid": "doctor-resend-invite-button",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (showResendConfirm.value = true))
                  }, [
                    _createVNode(SVGIcon, {
                      path: _unref(mdiEmailSyncOutline),
                      class: "text-sm"
                    }, null, 8, ["path"]),
                    _createTextVNode(" Reenviar convite ")
                  ], 8, _hoisted_8))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createVNode(ConfirmActionModal, {
      show: showResendConfirm.value,
      title: "Reenviar convite",
      description: `Deseja reenviar o convite para ${_ctx.link.company.name}? Um novo convite pendente será criado.`,
      "text-button": "Reenviar",
      "is-loading": isResending.value,
      onCloseModal: _cache[2] || (_cache[2] = ($event: any) => (showResendConfirm.value = false)),
      onHandleConfirm: handleResend
    }, null, 8, ["show", "description", "is-loading"])
  ], 64))
}
}

})